import { createI18n, type LocaleLoaders } from '@shared/i18n/config';
import { LANGUAGE_STORAGE_KEY } from '@shared/i18n/languages';
import enJson from '@/locales/en.json';

// One file per language, each holding this app's own namespaces (`common` + `status`).
// The KEYS of this map are the single source of truth for which languages the status page
// supports — adding a locale file plus a line here makes the language appear everywhere
// (picker, detection, supportedLngs), and removing a line removes it. This is fully
// independent of the other apps and of the global registry: the registry can list more
// languages than this app ships without breaking anything here.
//
// English is bundled eagerly (default + fallback); the others are code-split and loaded on
// demand the first time that language is selected, so the initial download never carries
// languages the visitor is not using.
const localeLoaders: LocaleLoaders = {
  es: () => import('@/locales/es.json'),
  fr: () => import('@/locales/fr.json'),
  de: () => import('@/locales/de.json'),
  ja: () => import('@/locales/ja.json'),
  pt: () => import('@/locales/pt.json'),
  'pt-BR': () => import('@/locales/pt-BR.json'),
  ar: () => import('@/locales/ar.json'),
  cs: () => import('@/locales/cs.json'),
  da: () => import('@/locales/da.json'),
  it: () => import('@/locales/it.json'),
  ko: () => import('@/locales/ko.json'),
  nl: () => import('@/locales/nl.json'),
  pl: () => import('@/locales/pl.json'),
  sv: () => import('@/locales/sv.json'),
};

// Read the persisted preference without throwing in private mode / blocked storage.
function readStoredLanguage(): string | null {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    return null;
  }
}

// The status page is fully public (no user record), so the language is resolved entirely
// client-side from the stored preference, then browser language, then English — matched
// against the set declared above.
export const i18n = createI18n({
  stored: readStoredLanguage(),
  defaultBundle: enJson,
  loaders: localeLoaders,
});
